import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Save, Loader, Clock, User, Phone, Mail, MapPin, Building, Briefcase, ChevronDown, ChevronRight, Send, CheckCircle, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, citiesService, sourcesService, prequalificationService, tendersService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { getActivityMeta, formatActivityMessage } from '../../utils/helpers';
import { PROFILE_STATUSES, APPROVAL_STATUS_CONFIG } from '../../lib/constants';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { SearchableCitySelect } from './ProfileModal';
import ApproveProfileModal from './ApproveProfileModal';
import RejectProfileModal from './RejectProfileModal';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import Toast from '../ui/Toast';

export default function ProfileDetailModal({
  profile,
  currentUser,
  users = [],
  onClose,
  onSave,
  onRefresh
}) {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('details');
  const [isSaving, setIsSaving] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const [formData, setFormData] = useState({ ...profile });
  const [referenceData, setReferenceData] = useState({ ...profile });

  const [cities, setCities] = useState([]);
  const [sources, setSources] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [comment, setComment] = useState('');

  const [timelineActivities, setTimelineActivities] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const activityEndRef = useRef(null);

  const [confirmInterested, setConfirmInterested] = useState(false);
  const [nextStep, setNextStep] = useState('Prequalification');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoadingCities(true);
    citiesService.getAll({ activeOnly: 'true' })
      .then(res => { if (isMounted) setCities(res); })
      .catch(console.error)
      .finally(() => { if (isMounted) setLoadingCities(false); });

    sourcesService.getAll()
      .then(res => { if (isMounted) setSources(res); })
      .catch(console.error);

    return () => { isMounted = false; };
  }, []);

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const activities = await contactService.getProfileTimeline(profile.id);
      setTimelineActivities(activities);
    } catch (error) {
      console.error('Error fetching profile timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.id) fetchTimeline();
  }, [profile?.id]);

  const timeline = useMemo(() => {
    return timelineActivities.map((a) => {
      let content = {};
      let raw = a.content;
      let isProfilePhase = false;
      if (typeof raw === 'string' && raw.startsWith('[Profile Phase] ')) {
        isProfilePhase = true;
        raw = raw.substring('[Profile Phase] '.length);
      }
      try {
        content = JSON.parse(raw);
      } catch (e) {
        content = { note: raw };
      }
      return {
        type: a.type?.toLowerCase() || 'note_added',
        data: content,
        timestamp: new Date(a.createdAt),
        user: a.user?.username || a.user?.name || 'System',
        isProfileHistory: !!a.isProfileHistory || isProfilePhase
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  }, [timelineActivities]);

  useEffect(() => {
    if (activeTab === 'timeline') activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab, timeline]);

  const handleCreateNewCity = async (cityName) => {
    const created = await citiesService.create({ name: cityName });
    if (created) {
      setCities((prev) => [...prev, created]);
      setFormData({ ...formData, city: created.name });
    } else {
      setFormData({ ...formData, city: cityName });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = { ...formData };
      
      const updated = await contactService.update(profile.id, payload);
      setReferenceData({ ...updated });
      if (onSave) onSave(updated);
      if (onRefresh) onRefresh();
      
      setToast({ type: 'success', message: t('profileUpdated') || 'Profile updated successfully!' });
      
      // Auto-refresh timeline if status changed
      if (formData.status !== referenceData.status) {
        fetchTimeline();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setToast({ type: 'error', message: t('errorUpdatingProfile') || 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      await contactService.addNote(profile.id, { note: comment.trim() });
      setComment('');
      setToast({ type: 'success', message: t('commentAdded') || 'Comment added successfully' });
      fetchTimeline();
    } catch (error) {
      console.error('Failed to add comment:', error);
      setToast({ type: 'error', message: t('errorAddingComment') || 'Failed to add comment' });
    }
  };

  const TABS = [
    { id: 'details', label: 'Details', labelUr: 'تفصیلات', icon: User },
    { id: 'timeline', label: 'Timeline', labelUr: 'ٹائم لائن', icon: Clock }
  ];

  const handleStageClick = (stage) => {
    if (stage === 'Interested') {
      if (formData.approvalStatus && formData.approvalStatus !== 'APPROVED') {
        setToast({
          type: 'error',
          message: 'Profile must be approved by GM Operations or CEO before creating Prequalification or Tender.'
        });
        return;
      }
      setConfirmInterested(true);
    } else {
      setFormData({ ...formData, status: stage });
    }
  };

  const handleApproveInModal = async ({ assignedToId, remarks }) => {
    try {
      const updated = await contactService.approve(profile.id, { assignedToId, remarks });
      setFormData({ ...updated });
      setReferenceData({ ...updated });
      if (onSave) onSave(updated);
      if (onRefresh) onRefresh();
      fetchTimeline();
      setToast({ type: 'success', message: 'Profile approved successfully!' });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to approve profile' });
      throw err;
    }
  };

  const handleRejectInModal = async (remarks) => {
    try {
      const updated = await contactService.reject(profile.id, remarks);
      setFormData({ ...updated });
      setReferenceData({ ...updated });
      if (onSave) onSave(updated);
      if (onRefresh) onRefresh();
      fetchTimeline();
      setToast({ type: 'success', message: 'Profile rejected and returned with comments.' });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to reject profile' });
      throw err;
    }
  };

  const handleResubmit = async () => {
    try {
      setIsResubmitting(true);
      const updated = await contactService.resubmit(profile.id, formData);
      setFormData({ ...updated });
      setReferenceData({ ...updated });
      if (onSave) onSave(updated);
      if (onRefresh) onRefresh();
      fetchTimeline();
      setToast({ type: 'success', message: 'Profile resubmitted for approval successfully!' });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to resubmit profile' });
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleConfirmNextStep = async () => {
    if (formData.approvalStatus && formData.approvalStatus !== 'APPROVED') {
      setToast({
        message: 'This profile must be approved by GM Operations or CEO before creating Prequalification or Tender.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSaving(true);
      if (nextStep === 'Prequalification') {
        await prequalificationService.create({
          profileId: profile.id,
          title: `${formData.projectName || formData.clientName || 'Client'} - Prequalification`,
          status: 'New'
        });
        setToast({ message: 'Prequalification created successfully!', type: 'success' });
      } else if (nextStep === 'Tender') {
        await tendersService.create({
          profileId: profile.id,
          title: `${formData.projectName || formData.clientName || 'Client'} - Tender`,
          type: 'TENDER',
          stage: 'In Process'
        });
        setToast({ message: 'Tender created successfully!', type: 'success' });
      } else if (nextStep === 'Proposal') {
        await tendersService.create({
          profileId: profile.id,
          title: `${formData.projectName || formData.clientName || 'Client'} - Proposal`,
          type: 'PROPOSAL',
          stage: 'In Process'
        });
        setToast({ message: 'Proposal created successfully!', type: 'success' });
      }
      
      const newFormData = { ...formData, status: 'Interested' };
      setFormData(newFormData);
      setConfirmInterested(false);
      
      const updated = await contactService.update(profile.id, newFormData);
      setReferenceData({ ...updated });
      if (onSave) onSave(updated);
      if (onRefresh) onRefresh();
      fetchTimeline();
    } catch (error) {
      console.error(error);
      setToast({ message: 'Failed to create. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 sm:p-6"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative"
        >
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-gray-900 truncate">
                    {formData.clientName || formData.contactPersonName || 'No Name'}
                  </h2>
                  <span className={`px-2.5 py-0.5 text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${
                    formData.status === 'Interested' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    formData.status === 'Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    formData.status === 'Lost' ? 'bg-red-50 text-red-700 border-red-200' :
                    (formData.status || 'New').includes('Ready') ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    formData.status === 'In Process' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }`}>
                    {formData.status || 'New'}
                  </span>
                  {formData.approvalStatus === 'APPROVED' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full border font-bold bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap" title={formData.approvedBy ? `Approved by ${formData.approvedBy.username}` : 'Approved Profile'}>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Approved
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
                  {formData.mobile && (
                    <span className="flex items-center gap-1 font-mono text-gray-700">
                      <Phone className="w-3.5 h-3.5" />{formData.mobile}
                    </span>
                  )}
                  {formData.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />{formData.email}
                    </span>
                  )}
                  {formData.assignedTo && (
                    <span className="flex items-center gap-1 text-gray-600">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      Assigned to <strong className="text-gray-800 font-semibold">{formData.assignedTo.username}</strong>
                    </span>
                  )}
                  {formData.approvedBy && (
                    <span className="text-gray-400 text-[11px]">
                      • Approved by {formData.approvedBy.username}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Stage Pills */}
            <div className="mt-4 flex flex-wrap gap-2">
              {PROFILE_STATUSES.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStageClick(status)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    (formData.status || 'New') === status
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>

            {/* Approval Workflow Banner */}
            {formData.approvalStatus === 'PENDING_APPROVAL' && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
                <div className="flex items-center gap-2.5 text-amber-900">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping shrink-0" />
                  <div>
                    <span className="font-bold">Pending Approval:</span> This profile is awaiting review by GM Operations or CEO.
                    {formData.createdBy && (
                      <span className="text-amber-700 ml-1 font-medium">
                        (Created by {formData.createdBy.username})
                      </span>
                    )}
                  </div>
                </div>
                {['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role) && (
                  <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowApproveModal(true)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Approve</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRejectModal(true)}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      <span>Reject</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {formData.approvalStatus === 'REJECTED' && (
              <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
                <div className="flex items-start gap-2.5 text-rose-900">
                  <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Profile Rejected / Revisions Requested</span>
                    {formData.approvalRemarks && (
                      <p className="mt-0.5 text-rose-700 font-medium">
                        "{formData.approvalRemarks}"
                      </p>
                    )}
                  </div>
                </div>
                {(formData.createdById === currentUser?.id || ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role)) && (
                  <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={isResubmitting}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all shrink-0 self-end sm:self-auto disabled:opacity-50"
                  >
                    {isResubmitting ? 'Resubmitting...' : 'Resubmit for Approval'}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
            {/* Sidebar */}
            <div className="w-full sm:w-48 bg-white border-r border-gray-200 p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-visible no-scrollbar shrink-0">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${isActive ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">{language === 'ur' ? tab.labelUr : tab.label}</span>
                    {tab.id === 'timeline' && timeline.length > 0 && (
                      <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{timeline.length}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Main scroll area */}
            <div className="flex-1 overflow-y-auto p-4 md:p-8">
              {activeTab === 'details' && (
                <div className="max-w-3xl mx-auto space-y-6 pb-6">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-gray-100 pb-3">
                      <User className="w-4 h-4 shrink-0" /> {t('profileHeading') || 'Profile Details'}
                    </h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('clientName') || 'Client Name'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterClientName') || 'Client Name'}
                          value={formData.clientName || ''}
                          onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('contactPerson') || 'Contact Person'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterContactPerson') || 'Contact Person'}
                          value={formData.contactPersonName || ''}
                          onChange={(e) => setFormData({ ...formData, contactPersonName: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('designation') || 'Designation'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterDesignation') || 'Designation'}
                          value={formData.designation || ''}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('projectName') || 'Project Name'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterProjectName') || 'Project Name'}
                          value={formData.projectName || ''}
                          onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('mobile') || 'Mobile Number'}</label>
                        <div className="mt-1" dir="ltr">
                          <PhoneInput
                            country={'pk'}
                            value={formData.mobile}
                            onChange={(val) => setFormData({ ...formData, mobile: val })}
                            containerClass="w-full"
                            inputClass="!w-full !py-5 !pl-12 !border-gray-200 !rounded-lg !text-sm"
                            buttonClass="!border-gray-200 !rounded-l-lg !bg-gray-50"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('email') || 'Email Address'}</label>
                        <input
                          type="email"
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder="email@example.com"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('city') || 'City'}</label>
                        <SearchableCitySelect
                          value={formData.city}
                          onChange={(val) => setFormData({ ...formData, city: val })}
                          cities={cities}
                          onCreateCity={handleCreateNewCity}
                          loading={loadingCities}
                          onToggleOpen={setIsCityDropdownOpen}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('source') || 'Source'}</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-200 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[38px] focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all outline-none">
                              {sources.find(s => String(s.name) === String(formData.source))?.name || formData.source || t('selectSource') || 'Select Source'}
                              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full z-[150]">
                            <DropdownMenu
                              selectedKeys={new Set([String(formData.source)])}
                              selectionMode="single"
                              onSelectionChange={(keys) => {
                                setFormData({ ...formData, source: Array.from(keys)[0] });
                              }}
                            >
                              {sources.length ? sources.map((s) => (
                                <DropdownItem key={s.id} id={String(s.name)}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('address') || 'Address'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterAddress') || 'Enter Address'}
                          value={formData.address || ''}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="max-w-2xl mx-auto flex flex-col h-full">
                  <div className="flex-1 space-y-4 mb-4 overflow-y-auto min-h-[400px]">
                    {timelineLoading ? (
                      [1, 2, 3].map(i => (
                        <div key={`sk-${i}`} className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Skeleton variant="circular" width={20} height={20} animation="wave" />
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                            <Skeleton width="60%" height={16} animation="wave" className="mb-2" />
                            <Skeleton width="40%" height={12} animation="wave" />
                          </div>
                        </div>
                      ))
                    ) : timeline.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="font-medium">{t('noActivityYet') || 'No activity yet'}</p>
                        <p className="text-xs mt-1 text-gray-300">Add a note below to start tracking.</p>
                      </div>
                    ) : (
                      timeline.map((item, i) => {
                        const meta = getActivityMeta(item.type);
                        return (
                          <div key={i} className="flex gap-3">
                            <div className={`w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                              <span className="text-sm">{meta.icon}</span>
                            </div>
                            <div className="flex-1 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                  {formatActivityMessage({ type: item.type, ...item.data })}
                                </p>
                                {item.isProfileHistory && (
                                  <span className="inline-block bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                    {t('profilePhase') || 'Profile Phase'}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                {item.user} &bull; {item.timestamp.toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={activityEndRef} />
                  </div>

                  {/* Add Note Input */}
                  <div className="flex gap-2 pt-4 border-t">
                    <input
                      className="flex-1 p-3 rounded-xl border outline-none text-sm"
                      placeholder={t('addNotePlaceholder') || "Type a note..."}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <button
                      onClick={handleAddComment}
                      className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Sticky Save Footer */}
          {activeTab === 'details' && (
            <div className="px-4 sm:px-6 py-3 bg-white border-t border-gray-200 flex justify-end gap-2 relative z-20 shrink-0">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-5 py-2.5 text-sm text-white font-semibold rounded-xl flex items-center gap-2 transition-all ${
                  isSaving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
                }`}
              >
                {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSaving ? (t('saving') || 'Saving...') : t('save')}
              </button>
            </div>
          )}
        </motion.div>

        {/* Interested Stage Confirmation Modal */}
        {confirmInterested && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b flex justify-between items-center bg-blue-50/50">
                <div>
                  <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    {language === 'ur' ? 'تصدیق' : 'Confirm Interested Stage'}
                  </h3>
                  <p className="text-sm text-blue-600 mt-0.5">
                    {language === 'ur' ? 'براہ کرم تفصیلات کی تصدیق کریں اور اگلا قدم منتخب کریں' : 'Please verify details and select the next step.'}
                  </p>
                </div>
                <button onClick={() => setConfirmInterested(false)} className="p-2 hover:bg-blue-100 rounded-full transition-colors">
                  <X className="w-5 h-5 text-blue-700" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
                <div className="max-w-lg mx-auto space-y-6">
                  {/* Profile Details Edit */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('profileHeading') || 'Profile Details'}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('clientName') || 'Client Name'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.clientName || ''}
                          onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('contactPerson') || 'Contact Person'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.contactPersonName || ''}
                          onChange={(e) => setFormData({ ...formData, contactPersonName: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('designation') || 'Designation'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.designation || ''}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('projectName') || 'Project Name'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.projectName || ''}
                          onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('mobile') || 'Mobile Number'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.mobile || ''}
                          onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('email') || 'Email Address'}</label>
                        <input
                          type="email"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('city') || 'City'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.city || ''}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t('address') || 'Address'}</label>
                        <input
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          value={formData.address || ''}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Next Step Selection */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{language === 'ur' ? 'اگلا قدم' : 'Next Step'}</h4>
                    <div className="space-y-3">
                      <label className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${nextStep === 'Prequalification' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="nextStep" checked={nextStep === 'Prequalification'} onChange={() => setNextStep('Prequalification')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm font-semibold text-gray-800">{language === 'ur' ? 'پری کوالیفکیشن بنائیں' : 'Create Prequalification'}</span>
                      </label>
                      <label className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${nextStep === 'Tender' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="nextStep" checked={nextStep === 'Tender'} onChange={() => setNextStep('Tender')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm font-semibold text-gray-800">{language === 'ur' ? 'ٹینڈر بنائیں' : 'Create Tender'}</span>
                      </label>
                      <label className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${nextStep === 'Proposal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="nextStep" checked={nextStep === 'Proposal'} onChange={() => setNextStep('Proposal')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm font-semibold text-gray-800">{language === 'ur' ? 'پروپوزل بنائیں' : 'Create Proposal'}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-white border-t flex justify-end gap-3 shrink-0">
                <button onClick={() => setConfirmInterested(false)} className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
                  {language === 'ur' ? 'منسوخ کریں' : 'Cancel'}
                </button>
                <button onClick={handleConfirmNextStep} disabled={isSaving} className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors flex items-center gap-2">
                  {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {language === 'ur' ? 'محفوظ کریں' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>

      {/* Approve Profile Modal */}
      {showApproveModal && (
        <ApproveProfileModal
          profile={formData}
          users={users}
          onConfirm={handleApproveInModal}
          onClose={() => setShowApproveModal(false)}
        />
      )}

      {/* Reject Profile Modal */}
      {showRejectModal && (
        <RejectProfileModal
          profile={formData}
          onConfirm={handleRejectInModal}
          onClose={() => setShowRejectModal(false)}
        />
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </AnimatePresence>
  );
}

